//////////////////
// IMPORTS
//////////////////
// schema
import * as ISchema from "@schema";
// libraries
import * as React from "react";
import Radium from "radium";
import { connect } from "react-redux";
import { createFragmentContainer } from "react-relay";
// email-triage-goals imports
import IGoalsWrapper from "./goals-wrapper.d";
import { BaseGoalComponent } from "./base-goal.component";
import * as styles from "./goals-wrapper.styles";
// other internals
import { logger } from "@util/logger.util";
import ReduxState from "../../declarations/state.d";
import { removeGoal, upsertFocusTime } from "@mutations";
import { standardGoals } from "@util/goal.util";
import { snackBarOpen } from "@actions/snack-bar.actions";
// material-ui
import {
  Checkbox,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  Switch,
} from "@material-ui/core";

const focusBlockTimes: number[] = [];
(function () {
  let time = 60;
  while (time <= 60 * 8) {
    focusBlockTimes.push(time);
    time += 30;
  }
})();

//////////////////
// COMPONENT
//////////////////
@Radium
export class FocusTimeGoalComponent extends BaseGoalComponent<IGoalsWrapper.IFocusTime.State> {
  constructor(props: IGoalsWrapper.Props) {
    super(props, standardGoals.FocusTime);

    this.state = this.calculateStateFromProps();

    this.onToggleSyncToGoogle = this.onToggleSyncToGoogle.bind(this);
    this.onFocusBlockDurationChanged = this.onFocusBlockDurationChanged.bind(this);
    this.onMutationFailure = this.onMutationFailure.bind(this);
  }

  private calculateStateFromProps(): IGoalsWrapper.IFocusTime.State {
    const goal = this.findGoal();
    const goalOptions = goal && goal.options;

    const withinWeekOptions =
      goalOptions && (goalOptions.specificOptions as ISchema.IGoalWithinWeekOptions);

    return {
      selected: !!goal,
      loading: false,
      syncToGoogle: (goalOptions && goalOptions.syncToGoogle) || false,
      maxMinutesPerBlock: (withinWeekOptions && withinWeekOptions.maxMinutesPerBlock) || 180,
    };
  }

  private createFocusTimeOptions(
    newOptions: Partial<IGoalsWrapper.IFocusTime.State> = this.state,
  ): ISchema.IGoalPatchFocusTimeOptionsInput {
    return {
      syncToGoogle: newOptions.syncToGoogle,
      maxMinutesPerBlock: newOptions.maxMinutesPerBlock,
    };
  }

  // Handlers

  private onUpdatePreferencesSuccess = () => {
    this.props.snackBarOpen("Updated preferences");
  };

  private onUpdatePreferencesFailure = () => {
    this.props.snackBarOpen("An error occurred!");
  };

  private onMutateOnToggleSuccess = (isInputChecked: boolean) => {
    this.setState({ loading: false });
    this.props.snackBarOpen(`${isInputChecked ? "Added" : "Removed"} "Focus Time" goal`);
  };

  private updatePreferences(newPreferences: Partial<IGoalsWrapper.IFocusTime.State>): void {
    const options = this.createFocusTimeOptions(newPreferences);
    const org = this.props.org;

    upsertFocusTime(
      this.props.relay.environment,
      { options, orgId: org.id },
      this.onUpdatePreferencesSuccess,
      this.onUpdatePreferencesFailure,
    );
  }

  protected mutateOnToggle(isInputChecked: boolean): void {
    const org = this.props.org;

    if (isInputChecked) {
      this.setState({ loading: true });
      upsertFocusTime(
        this.props.relay.environment,
        { orgId: org.id, options: this.createFocusTimeOptions() },
        this.onMutateOnToggleSuccess.bind(this, isInputChecked),
        this.onMutationFailure,
      );
    } else {
      const goal = this.findGoalInput();
      if (goal) {
        this.setState({ loading: true });
        removeGoal(
          this.props.relay.environment,
          { goal, orgId: org.id },
          this.onMutateOnToggleSuccess.bind(this, isInputChecked),
          this.onMutationFailure,
        );
      } else {
        logger.debug("Couldn't find goal to remove");
      }
    }
  }

  private onToggleSyncToGoogle(
    _event: React.ChangeEvent<HTMLInputElement>,
    isInputChecked: boolean,
  ): void {
    const newState = { syncToGoogle: isInputChecked };

    this.setState(newState);
    this.updatePreferences(newState);
  }

  private onFocusBlockDurationChanged(e: React.ChangeEvent<HTMLSelectElement>): void {
    const maxMinutesPerBlock = parseInt(e.target.value);
    const newState = { maxMinutesPerBlock };

    this.setState(newState);
    this.updatePreferences(newState);
  }

  private onMutationFailure(): void {
    this.props.snackBarOpen("An error occurred!");
  }

  private renderFocusBlockTimes(): JSX.Element[] {
    return focusBlockTimes.map((time) => (
      <MenuItem key={time} value={time}>
        {time > 60 ? `${time / 60} hours` : `${time} minutes`}
      </MenuItem>
    ));
  }

  private renderFocusTimeOptions() {
    const disabled = !this.state.selected || this.state.loading;
    return (
      <div style={styles.container}>
        <FormControl>
          <InputLabel shrink htmlFor="cw-focus-block-duration-select">
            Max Length of contiguous time
          </InputLabel>
          <Select
            value={this.state.maxMinutesPerBlock}
            onChange={this.onFocusBlockDurationChanged}
            name="Max Length of contiguous time"
            disabled={disabled}
            inputProps={{
              name: "Max Length of contiguous time",
              id: "cw-focus-block-duration-select",
            }}
          >
            {this.renderFocusBlockTimes()}
          </Select>
        </FormControl>
        <div style={styles.checkboxContainer}>
          <FormControl>
            <FormControlLabel
              control={<Checkbox color="primary" checked={this.state.syncToGoogle} />}
              onChange={this.onToggleSyncToGoogle}
              disabled={disabled}
              label="Schedule a Smart Hold on Google calendar to protect this time"
              labelPlacement="end"
            />
          </FormControl>
        </div>
      </div>
    );
  }

  public render(): JSX.Element {
    return (
      <div style={styles.goal}>
        <div style={styles.toggleContainer}>
          <FormControl>
            <FormControlLabel
              control={<Switch color="primary" checked={this.state.selected} />}
              onChange={this.onToggle}
              disabled={this.state.loading}
              label="Open up blocks of contiguous time on my calendar"
              labelPlacement="end"
            />
          </FormControl>
        </div>
        {this.renderFocusTimeOptions()}
      </div>
    );
  }
}

//////////////////
// REDUX
//////////////////
function mapStateToProps(_state: ReduxState.Redux, _ownProps: IGoalsWrapper.Container) {
  return {}; // add redux state here, if needed
}

const reduxContainer = connect(mapStateToProps, {
  snackBarOpen,
})(FocusTimeGoalComponent);

//////////////////
// RELAY
//////////////////
import { fragments } from "./FocusTimeGoal.gql";
export const FocusTimeGoal = createFragmentContainer(reduxContainer, fragments);
